import { EventEmitter } from 'events'

import debounce from 'lodash/debounce'
import partition from 'lodash/partition'

import { getComposerStore } from '~publish/components/Composer/store/composerStore'
import type {
  ComposerProfile,
  Subprofile,
  ValidationCode,
  AppState,
  AppStoreMetaData,
  AppStoreOptions,
  AppStoreState,
  ValidationFeedback,
} from '~publish/legacy/composer/composer/stores/types'
import type { ServiceDefinition } from '~publish/legacy/constants/services/types'
import { isNotUndefined } from '~publish/legacy/utils/isNotUndefined/isNotUndefined'
import { VALIDATION_CODE } from '~publish/legacy/validation/constants'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { getRelaxDraftValidationEnabled } from '../lib/validation/relaxDraftValidationControl'
import ComposerStore from './ComposerStore'
import { getComposerFeedbackMessages } from './utils'
import {
  PostTypeFacebookReel,
  PostTypeFacebookStory,
} from '../../../post/constants'
import AppActionCreators from '../action-creators/AppActionCreators'
import ComposerActionCreators from '../action-creators/ComposerActionCreators'
import {
  AsyncOperationStates,
  COMPOSER_ERROR_CODE,
  Services,
} from '../AppConstants'
import AppDispatcher from '../dispatcher'

// import { registerStore, sendToMonitor } from '../utils/devtools';

import type { Draft } from '../entities/Draft'
import { getNewSubprofile } from '../entities/factories'
import { ActionTypes } from '../state/ActionTypes'
import { getWiredComposerStore } from '../state/getWiredComposerStore'
import type { ComposerDispatchPayload } from '../types'

getWiredComposerStore()

const CHANGE_EVENT = 'change'

const DEFAULT_MASTODON_CHARACTER_LIMIT = 500

const getInitialState = (): AppStoreState => ({
  profiles: [], // Data structure in getNewProfile()
  connectedChannels: [],
  // Describe the application state
  appState: {
    isLoaded: false,
    expandedComposerId: null,
    expandedProfileSubprofileDropdownId: null,
    isThumbnailLoading: false,
    isSavingPossible: false,
    isSavingDraftPossible: false,
    whatPreventsSaving: [], // Data structure in getNewPreventsSavingObj()
    whatPreventsSavingDraft: [],
    composersHaveBeenExpanded: false,
    // Ids of profiles whose subprofile dropdowns we want to auto-expand
    profileSubprofileDropdownsIdsToExpand: [],
    // true if enabled, false if enabled then disabled thru "customize" button, null if unused
    isOmniboxEnabled: null,
    // Profile ids that were already saved from drafts that failed saving to all profiles
    partiallySavedDraftsProfilesIds: new Map(), // draft id <=> [savedProfileIds]
    domainsOwnedByFacebookPages: new Map(), // url <=> (profile id <=> isOwned)
    rightSidePanelVisibleOnTop: null,
  },

  metaData: {},
  options: {},
})

let state = getInitialState()

// Keep stable reference to avoid recreating arrays on every call
let lastSelectedProfiles: ComposerProfile[] | null = null
let lastSelectionKey: string | null = null

// Register with Redux DevTools (uncomment to enable)
// registerStore('app', state);

/**
 * Pure so callers can pass a Zustand-sourced profile list instead of going
 * through `AppStore.getSelectedProfiles()` — `connectedChannels` (all of the
 * org's channels, not just selected ones) is a separate concern, still Flux
 * only, and unaffected either way.
 */
export function computeApprovalRequirement(
  selectedProfiles: ComposerProfile[],
  connectedChannels: AppStoreState['connectedChannels'],
): 'all' | 'some' | 'none' {
  const profileRequiresApproval = (profile: ComposerProfile): boolean => {
    const channel = connectedChannels.find(
      (matchingChannel) => matchingChannel.id === profile.id,
    )
    return !canPerform(channel, 'scheduleUpdates')
  }

  // the 'every' check below will return true for empty arrays
  // so we need to handle the empty array case first
  if (selectedProfiles.length === 0) {
    return 'none'
  }

  if (selectedProfiles.every(profileRequiresApproval)) {
    return 'all'
  }

  if (selectedProfiles.some(profileRequiresApproval)) {
    return 'some'
  }

  return 'none'
}

const AppStore = {
  ...EventEmitter.prototype,
  resetState: (): void => {
    state = getInitialState()
  },
  emitChange: (): boolean => AppStore.emit(CHANGE_EVENT),
  addChangeListener: (callback: (args?: unknown) => void): EventEmitter =>
    AppStore.on(CHANGE_EVENT, callback),
  removeChangeListener: (callback: (args?: unknown) => void): EventEmitter =>
    AppStore.removeListener(CHANGE_EVENT, callback),
  getAppState: (): AppState => state.appState,
  getMetaData: (): AppStoreMetaData | Record<string, never> => state.metaData,
  getOptions: (): AppStoreOptions | Record<string, never> => state.options,
  hasProfiles: (): boolean => {
    return state.profiles.length > 0
  },
  getConnectedChannels: (): AppStoreState['connectedChannels'] =>
    state.connectedChannels ?? [],
  getSelectedMastodonMaxCharacters: (): number | undefined => {
    const selectedProfiles = AppStore.getSelectedProfilesForService('mastodon')
    if (selectedProfiles.length === 0) {
      return undefined
    }

    const connectedChannels = AppStore.getConnectedChannels()
    const limits = selectedProfiles.map((profile) => {
      const channel = connectedChannels.find((c) => c.id === profile.id)
      // eslint-disable-next-line no-underscore-dangle -- GraphQL discriminator field
      return channel?.metadata?.__typename === 'MastodonMetadata'
        ? channel.metadata.maxCharacters
        : DEFAULT_MASTODON_CHARACTER_LIMIT
    })

    return Math.min(...limits)
  },
  requiresApprovalForSelectedProfiles: (): 'all' | 'some' | 'none' =>
    computeApprovalRequirement(
      AppStore.getSelectedProfiles(),
      AppStore.getConnectedChannels(),
    ),

  // Get all profiles if no param passed, or profiles matching a passed array of ids
  getProfiles: (ids?: string[]): ComposerProfile[] => {
    let profiles

    if (!ids) {
      profiles = state.profiles
    } else {
      profiles = state.profiles
        .filter((profile) => ids.includes(profile.id))
        .toSorted((profileA, profileB) =>
          ids.indexOf(profileA.id) < ids.indexOf(profileB.id) ? -1 : 1,
        )
    }

    return profiles
  },

  whatPreventsSaving: (): ValidationFeedback[] =>
    state.appState.whatPreventsSaving,

  getProfileIds: (): string[] =>
    AppStore.getProfiles().map((profile) => profile.id),

  getProfilesForService: (serviceName?: string | null): ComposerProfile[] =>
    AppStore.getProfiles().filter(
      (profile) => profile.service.name === serviceName,
    ),

  getSelectedProfiles: (): ComposerProfile[] => {
    // Performance optimization: Return stable array reference when selection hasn't changed.
    // Without this, .filter() creates a new array on every call, causing React components
    // with useMemo/useEffect dependencies on this array to re-render/recalculate unnecessarily.

    // Create a key representing current selection state. `selectedSubprofileId`
    // is part of it: the Pinterest board is a per-profile selection that mutates
    // in place without touching `isSelected`, and the GraphQL save path derives
    // the board through memos keyed on this array reference. Omitting it froze
    // the saved board on the first selection, sending every Pin to that board.
    const currentSelectionKey = state.profiles
      .map((p) => `${p.id}:${p.isSelected}:${p.selectedSubprofileId}`)
      .join(',')

    // Return same array reference if selection hasn't changed
    if (lastSelectedProfiles && lastSelectionKey === currentSelectionKey) {
      return lastSelectedProfiles
    }

    // Selection changed, create new filtered array
    lastSelectedProfiles = state.profiles.filter(
      (profile) => profile.isSelected,
    )
    lastSelectionKey = currentSelectionKey

    return lastSelectedProfiles
  },

  hasProfilesSelected: (): boolean => {
    const selectedProfiles = AppStore.getSelectedProfiles()
    return selectedProfiles.length > 0
  },

  hasPinterestBoardsSelected: (): boolean => {
    const selectedPinterestProfiles = state.profiles.filter(
      (profile) => profile.isSelected && profile.service.name === 'pinterest',
    )

    if (selectedPinterestProfiles.length === 0) {
      return true
    }

    return selectedPinterestProfiles
      .map((profile) => !!profile.selectedSubprofileId)
      .reduce((prev, current) => prev && current)
  },

  hasFBDraftWithNoText: (): boolean => {
    const emptyFacebookDrafts = ComposerStore.getEnabledDrafts().filter(
      (draft) => draft.id === 'facebook' && draft.text.length < 1,
    )
    return emptyFacebookDrafts.length > 0
  },

  getSelectedProfilesForService: (serviceName: string): ComposerProfile[] =>
    AppStore.getSelectedProfiles().filter(
      (profile) => profile.service.name === serviceName,
    ),

  getProfile: (id: string): ComposerProfile | undefined =>
    state.profiles.find((profile) => profile.id === id),

  getServicesWithSelectedProfiles: (): ServiceDefinition[] => {
    const selectedProfilesServicesNames = AppStore.getSelectedProfiles().map(
      (profile) => profile.service.name,
    )

    const dedupedServicesNames = [...new Set(selectedProfilesServicesNames)]
    const selectedServices = dedupedServicesNames
      .map((serviceName) => Services.get(serviceName))
      .filter(isNotUndefined)

    return selectedServices
  },

  getSidebarVisibleOnTop: (): string | null =>
    state.appState.rightSidePanelVisibleOnTop,
  getExpandedComposerId: (): string | null =>
    AppStore.getIsOmniboxEnabled() ? 'omni' : state.appState.expandedComposerId,
  setExpandedComposerId: (id: string | null): void => {
    setExpandedComposerId(id)
  },
  areAllSelectedProfilesIG: (): boolean => {
    const notInstagram = AppStore.getSelectedProfiles().some(
      (profile) => profile.service.name !== 'instagram',
    )

    return !notInstagram || AppStore.getExpandedComposerId() === 'instagram'
  },
  getComposerFeedbackMessages: (draft: Draft): ValidationFeedback[] => {
    const draftMode = getComposerStore().getState().draftMode
    const whatPreventsSaving = draftMode
      ? state.appState.whatPreventsSavingDraft
      : state.appState.whatPreventsSaving

    return getComposerFeedbackMessages(whatPreventsSaving, draft.id)
  },

  // Getter and setter for isOmniboxEnabled
  // - null if omnibox was not shown in this composer session
  // - true when omnibox is the current enabled composer screen
  // - false when after omnibox screen, when "Customize for each network" is clicked
  getIsOmniboxEnabled: (): boolean | null => {
    // Return values that pertain to preview mode
    if (state.options.previewMode) {
      return AppStore.getSelectedProfiles().length === 0
    }

    return state.appState.isOmniboxEnabled ?? null
  },
  setIsOmniboxEnabled: (isEnabled: boolean): void => {
    state.appState.isOmniboxEnabled = isEnabled
  },
  getIsPreviewMode: (): boolean => {
    return state.options.previewMode
  },
}

// Auto-expand composer when only 1 profile is selected
const maybeAutoExpandComposer = (): void => {
  const activeServices = AppStore.getServicesWithSelectedProfiles()
  const hasOneActiveService = activeServices.length === 1
  if (!hasOneActiveService) return

  const activeService = activeServices[0]
  const composerId = activeService.name
  ComposerActionCreators.expand(composerId)
}

const updateShortenerForProfile = (profileId: string, domain: string): void => {
  const profile = AppStore.getProfile(profileId)
  if (!profile) {
    return
  }
  profile.shorteningDomain = domain

  state.profiles = state.profiles.map((p) => (p.id === profileId ? profile : p))

  AppActionCreators.markAppAsLoaded()
}

const refreshFacebookDomainOwnershipData = (): void => {
  if (!state.appState.isLoaded) return

  // Don't check more than 5 pages to save on Facebook API request quota
  const maxPagesToCheckCount = 5

  if (!ComposerStore.isDraftEnabled('facebook')) return
  if (!ComposerStore.doesDraftHaveLinkAttachmentEnabled('facebook')) return

  const selectedFacebookProfiles =
    AppStore.getSelectedProfilesForService('facebook')
  const selectedFacebookPages = selectedFacebookProfiles.filter(
    (profile) => profile.serviceType === 'page',
  )
  if (selectedFacebookPages.length > maxPagesToCheckCount) return

  const selectedPages = selectedFacebookPages.slice(0, maxPagesToCheckCount)
  if (selectedPages.length === 0) return

  const linkUrl = ComposerStore.getDraftLinkUrl('facebook')
  if (!linkUrl) return

  const domainOwnershipPerProfile =
    state.appState.domainsOwnedByFacebookPages.get(linkUrl) || new Map()

  const pendingRequestsCountForDomain = Array.from(
    domainOwnershipPerProfile.values(),
  ).filter((isOwner) => isOwner === null).length
  if (pendingRequestsCountForDomain >= maxPagesToCheckCount) return

  const selectedProfilesToQueryFacebookForOwnership = selectedPages.filter(
    ({ id }) => domainOwnershipPerProfile.get(id) === undefined,
  )

  selectedProfilesToQueryFacebookForOwnership.forEach(({ id }) => {
    updateFacebookDomainOwnershipForPage(id, linkUrl, false)

    AppActionCreators.getFacebookDomainOwnershipForProfile(id, linkUrl)
  })
}

/**
 * Select profiles in place of user, by either opening dropdown for subprofile-enabled
 * networks, or actually selecting the profile straight away for other networks.
 */
const selectProfilesOnBehalfOfUser = (
  ids: string[],
  markAppAsLoadedWhenDone = false,
  originatedFromGroupSelection = false,
): void => {
  const profilesToSelect = AppStore.getProfiles(ids)

  const [profilesWithSubprofiles, profilesWithoutSubprofiles]: [
    ComposerProfile[],
    ComposerProfile[],
  ] = partition(
    profilesToSelect,
    (profile: ComposerProfile) => profile.subprofiles.length > 0,
  )

  const hasProfilesWithoutSubprofiles = profilesWithoutSubprofiles.length > 0
  const hasProfilesWithSubprofiles = profilesWithSubprofiles.length > 0

  if (hasProfilesWithoutSubprofiles) {
    const profilesIdsWithoutSubprofiles = profilesWithoutSubprofiles.map(
      (profile) => profile.id,
    )

    AppActionCreators.selectProfiles(
      profilesIdsWithoutSubprofiles,
      markAppAsLoadedWhenDone,
      originatedFromGroupSelection,
    )
  } else if (markAppAsLoadedWhenDone) {
    AppActionCreators.markAppAsLoaded()
  }

  if (hasProfilesWithSubprofiles) {
    const draft = ComposerStore.getDraft('pinterest')

    if (draft?.isEmpty()) {
      profilesWithSubprofiles.forEach((profile) => {
        AppActionCreators.selectSubprofile(
          profile.id,
          profile.subprofiles[0].id,
        )
        AppActionCreators.collapseProfileSubprofileDropdown(profile.id)
        return profile.id
      })
    }
  }

  // we need to set isTwitterPremium flag on twitter drafts for increased char limit
  // to work, as it checks draft.isTwitterPremium to determine it
  const twitterProfile = profilesToSelect?.find(
    (profile) => profile.service.name === 'twitter',
  )
  if (twitterProfile) {
    ComposerActionCreators.setIsTwitterPremium(
      'twitter',
      twitterProfile.isTwitterPremium ?? false,
    )
  }

  const mastodonProfile = profilesToSelect?.find(
    (profile) => profile.service.name === 'mastodon',
  )
  if (mastodonProfile) {
    ComposerActionCreators.setMastodonMaxCharacters(
      'mastodon',
      AppStore.getSelectedMastodonMaxCharacters(),
    )
  }
}

const selectProfile = (
  id: string,
  markAppAsLoadedWhenDone = false,
  _originatedFromGroupSelection = false,
): void => {
  const profile = AppStore.getProfile(id)

  if (!profile) return

  if (profile && profile.isSelected) {
    if (markAppAsLoadedWhenDone) AppActionCreators.markAppAsLoaded()
    return
  }
  if (profile.isDisabled) return

  // Select profile
  profile.isSelected = true

  // Enable composer if the selected profile is the first selected for that service
  const serviceName = profile.service.name
  const selectedProfilesForService =
    AppStore.getSelectedProfilesForService(serviceName)

  if (selectedProfilesForService.length === 1) {
    const composerId = serviceName
    ComposerActionCreators.enable(composerId, markAppAsLoadedWhenDone)
    ComposerActionCreators.parseDraftTextLinks(composerId)
  } else if (markAppAsLoadedWhenDone) {
    AppActionCreators.markAppAsLoaded()
  }

  if (serviceName === 'twitter') {
    ComposerActionCreators.setIsTwitterPremium(
      serviceName,
      !!profile.isTwitterPremium,
    )
  }

  if (serviceName === 'mastodon') {
    ComposerActionCreators.setMastodonMaxCharacters(
      serviceName,
      AppStore.getSelectedMastodonMaxCharacters(),
    )
  }

  if (serviceName === 'facebook') {
    // Deliberately not flipped behind composer-zustand-editor-state: AppStore
    // is a Flux singleton with no React/hook context to check the flag from,
    // and the identity mirror (CORE-7187 Phase 1) guarantees editorSlice
    // holds this exact same BufferEditor reference regardless — reading
    // either side is behaviorally identical, so there's nothing to gain by
    // introducing non-React flag-check infra just for this call site.
    const editor = ComposerStore.getDraft(serviceName)?.editorState
    if (editor) editor.areMentionsSupported?.(editor)
    refreshFacebookDomainOwnershipData()
  }

  // We apply the omni update if we're in preview mode and only one profile is selected
  // To replicate the same behaviour as Customize for each network
  if (
    AppStore.getIsPreviewMode() &&
    AppStore.getSelectedProfiles().length === 1
  ) {
    ComposerActionCreators.applyOmniUpdate()
  }
}

const unselectProfile = (id: string): void => {
  const profile = AppStore.getProfile(id)
  if (!profile?.isSelected) return

  // Unselect profile
  profile.isSelected = false

  // Unselect subprofile if any (profiles can be unselected via unselectSubprofile() or directly)
  profile.selectedSubprofileId = null

  // Reset Facebook post type to Post when remaining selection is groups-only,
  // since Reels and Stories are only available for Pages (CONT-1619)
  const serviceName = profile.service.name
  if (serviceName === 'facebook') {
    const remainingFacebookProfiles =
      AppStore.getSelectedProfilesForService('facebook')
    const onlyGroupsRemain =
      remainingFacebookProfiles.length > 0 &&
      remainingFacebookProfiles.every((p) => p.serviceType === 'group')
    if (onlyGroupsRemain) {
      const facebookDraft = ComposerStore.getDraft('facebook')
      if (
        facebookDraft &&
        (facebookDraft.updateType === PostTypeFacebookReel ||
          facebookDraft.updateType === PostTypeFacebookStory)
      ) {
        facebookDraft.updateType = 'post'
      }
    }
  }

  // Disable composer if the unselected profile was the last selected for that service
  const selectedProfilesCountForService =
    AppStore.getSelectedProfilesForService(serviceName).length

  if (selectedProfilesCountForService === 0) {
    const composerId = serviceName
    ComposerActionCreators.disable(composerId)
  }

  maybeAutoExpandComposer()

  if (serviceName === 'facebook') refreshFacebookDomainOwnershipData()
}

const selectSubprofile = (profileId: string, subprofileId: string): void => {
  const profile = AppStore.getProfile(profileId)

  if (profile) {
    profile.selectedSubprofileId = subprofileId
    AppActionCreators.selectProfile(profile.id)
  }
}

const selectSubprofiles = (
  idsMap: Map<string, string>,
  markAppAsLoadedWhenDone = false,
): void => {
  const profileIds: string[] = []

  idsMap.forEach((subprofileId, profileId) => {
    const profile = AppStore.getProfile(profileId)
    if (profile) profile.selectedSubprofileId = subprofileId

    profileIds.push(profileId)
  })

  AppActionCreators.selectProfiles(profileIds, markAppAsLoadedWhenDone)
}

const unselectSubprofile = (profileId: string): void => {
  const profile = AppStore.getProfile(profileId)

  if (profile) {
    profile.selectedSubprofileId = null
    AppActionCreators.unselectProfile(profile.id)
    // This path bypasses ChannelSelector.tsx's dual-write, so mirror it here too.
    getComposerStore().getState().deselectChannel(profile.id)
  }
}

const preventSavingMessages = {
  AT_LEAST_ONE_PROFILE: 'At least one profile should be selected',
  AT_LEAST_ONE_BOARD:
    'One of your Pinterest profiles doesn’t have a board selected. Create a board and then try reselecting your profile.',
  WAITING_FOR_THUMBNAIL: 'Waiting for thumbnail to save...',
  NO_MORE_UPDATES_TO_SAVE: 'No more updates to be saved',
  SCHEDULE_IN_THE_PAST: 'The scheduled time seems to be in the past',
  SCHEDULE_IN_THE_PAST_DRAFT:
    'The scheduled time is in the past. Please choose a future time before saving.',
  NO_PERMISSION:
    "Your account is set to 'Approval Required' for this channel - please save as a draft to request approval. ",
}

export interface CheckIfSavingPossibleResult {
  isSavingPossible: boolean
  isSavingDraftPossible: boolean
  whatPreventsSaving?: ValidationFeedback[]
  whatPreventsSavingDraft?: ValidationFeedback[]
}

// Per-asset format-validity codes (aspect ratio, dimensions, file size, GIF
// frame count, video size/duration/frame-rate) plus the post-text character
// limit. Under relax-draft-validation these surface as warnings but do not
// block a draft save; every other code (missing text/media) still blocks.
const draftFormatValidationCodes: ReadonlySet<ValidationCode> = new Set([
  VALIDATION_CODE.ASPECT_RATIO,
  VALIDATION_CODE.FILE_SIZE,
  VALIDATION_CODE.IMAGE_SIZE,
  VALIDATION_CODE.GIF_FRAME_COUNT,
  VALIDATION_CODE.VIDEO_SIZE,
  VALIDATION_CODE.VIDEO_DURATION,
  VALIDATION_CODE.VIDEO_FRAME_RATE,
  VALIDATION_CODE.CHARACTER_LIMIT_REACHED,
])

// A sent post has no schedule to violate; anything else with a scheduled
// time in the past cannot be saved by the backend.
const isScheduledInThePast = (): boolean => {
  const scheduledAt = ComposerStore.isSentPost()
    ? null
    : ComposerStore.getScheduledAt()
  return scheduledAt !== null && scheduledAt <= Math.floor(Date.now() / 1000)
}

// List of conditions to pass in order to be able to save the updates
// For perf reasons all the checks aren't run all the time, we try to fail as early
// as possible, though in some situations it's useful to have multiple checks run
// to provide more complete feedback.
const checkIfSavingPossible = (): CheckIfSavingPossibleResult => {
  const savingNotPossible = {
    isSavingPossible: false,
    isSavingDraftPossible: false,
  }

  // At least one profile should be selected
  if (!AppStore.hasProfilesSelected()) {
    const whatPreventsSaving = [
      {
        code: COMPOSER_ERROR_CODE.MISSING_PROFILE,
        message: preventSavingMessages.AT_LEAST_ONE_PROFILE,
        composerId: '',
      },
    ]
    return {
      ...savingNotPossible,
      whatPreventsSaving,
      whatPreventsSavingDraft: whatPreventsSaving,
    }
  }

  if (!AppStore.hasPinterestBoardsSelected()) {
    const whatPreventsSaving = [
      {
        code: COMPOSER_ERROR_CODE.MISSING_PROFILE,
        message: preventSavingMessages.AT_LEAST_ONE_BOARD,
        composerId: '',
      },
    ]
    return {
      ...savingNotPossible,
      whatPreventsSaving,
      whatPreventsSavingDraft: whatPreventsSaving,
    }
  }

  // Updates shouldn't be saved while Instagram thumbnail is uploading
  if (state.appState.isThumbnailLoading) {
    const whatPreventsSaving = [
      {
        code: COMPOSER_ERROR_CODE.PROCESSING,
        message: preventSavingMessages.WAITING_FOR_THUMBNAIL,
        composerId: '',
      },
    ]
    return {
      ...savingNotPossible,
      whatPreventsSaving,
      whatPreventsSavingDraft: whatPreventsSaving,
    }
  }

  // Updates shouldn't have been saved already
  if (ComposerStore.areAllDraftsSaved()) {
    const whatPreventsSaving = [
      {
        code: COMPOSER_ERROR_CODE.ALREADY_SAVED,
        message: preventSavingMessages.NO_MORE_UPDATES_TO_SAVE,
        composerId: '',
      },
    ]
    return {
      ...savingNotPossible,
      whatPreventsSaving,
      whatPreventsSavingDraft: whatPreventsSaving,
    }
  }

  // Enabled posts shouldn't be empty
  const invalidEnabledPostsInfo = ComposerStore.getInvalidEnabledDraftsFeedback(
    { isDraft: false },
  )
  const invalidEnabledDraftsInfo =
    ComposerStore.getInvalidEnabledDraftsFeedback({ isDraft: true })

  const hasChannelConflicts =
    hasMultipleFacebookChannelsSelected() ||
    hasMultipleTwitterChannelsSelected() ||
    hasConflictingInstagramChannelsSelected()

  if (hasChannelConflicts) {
    const whatPreventsSaving = [...invalidEnabledPostsInfo]
    const whatPreventsSavingDraft = [...invalidEnabledDraftsInfo]

    if (hasMultipleFacebookChannelsSelected()) {
      const message =
        'You can only post to one Facebook channel type (Page or Group) at a time'
      whatPreventsSaving.push({
        message,
        composerId: '',
        code: VALIDATION_CODE.FACEBOOK_DIFFERENT_PROFILE_TYPES,
      })
      whatPreventsSavingDraft.push({
        message,
        composerId: '',
        code: VALIDATION_CODE.FACEBOOK_DIFFERENT_PROFILE_TYPES,
      })
    }

    if (hasMultipleTwitterChannelsSelected()) {
      const message = 'You can only post to one X/Twitter account at a time.'
      whatPreventsSaving.push({
        message,
        composerId: '',
        code: VALIDATION_CODE.TWITTER_MULTIPLE_ACCOUNTS,
      })
      whatPreventsSavingDraft.push({
        message,
        composerId: '',
        code: VALIDATION_CODE.TWITTER_MULTIPLE_ACCOUNTS,
      })
    }

    if (hasConflictingInstagramChannelsSelected()) {
      const message =
        'You can only post to one Instagram account type (Business or Personal) at a time'
      whatPreventsSaving.push({
        message,
        composerId: '',
        code: VALIDATION_CODE.INSTAGRAM_CONFLICTING_PROFILE_TYPES,
      })
      whatPreventsSavingDraft.push({
        message,
        composerId: '',
        code: VALIDATION_CODE.INSTAGRAM_CONFLICTING_PROFILE_TYPES,
      })
    }

    return {
      isSavingPossible: false,
      isSavingDraftPossible: false,
      whatPreventsSaving,
      whatPreventsSavingDraft,
    }
  }

  const hasInvalidEnabledPosts = invalidEnabledPostsInfo.length > 0
  const hasInvalidEnabledDrafts = invalidEnabledDraftsInfo.length > 0

  if (hasInvalidEnabledPosts || hasInvalidEnabledDrafts) {
    // whatPreventsSavingDraft keeps the full, unfiltered list so warnings
    // still surface in the composer; only the save-blocking boolean drops
    // format-only failures.
    const draftBlockingInfo = getRelaxDraftValidationEnabled()
      ? invalidEnabledDraftsInfo.filter(
          (feedback) => !draftFormatValidationCodes.has(feedback.code),
        )
      : invalidEnabledDraftsInfo

    const whatPreventsSavingDraft: ValidationFeedback[] = [
      ...invalidEnabledDraftsInfo,
    ]
    let isSavingDraftPossible = draftBlockingInfo.length === 0

    // Returning here skips the scheduled-in-the-past check below, so a draft
    // whose failures are all format-only must still be blocked when overdue —
    // the backend cannot save it.
    if (
      isSavingDraftPossible &&
      hasInvalidEnabledDrafts &&
      isScheduledInThePast()
    ) {
      isSavingDraftPossible = false
      whatPreventsSavingDraft.push({
        code: COMPOSER_ERROR_CODE.SCHEDULE_IN_THE_PAST,
        message: preventSavingMessages.SCHEDULE_IN_THE_PAST_DRAFT,
        composerId: '',
      })
    }

    return {
      isSavingPossible: false,
      isSavingDraftPossible,
      whatPreventsSaving: invalidEnabledPostsInfo,
      whatPreventsSavingDraft,
    }
  }

  // Check if user has permission for selected channels
  if (AppStore.requiresApprovalForSelectedProfiles() !== 'none') {
    const whatPreventsSaving = [
      {
        code: COMPOSER_ERROR_CODE.NO_PERMISSION,
        message: preventSavingMessages.NO_PERMISSION,
        composerId: '',
        metadata: {
          learnMoreText: 'Learn more about permissions here',
          learnMoreLink:
            'https://support.buffer.com/articles/adding-users-and-setting-up-permissions-ixjiLpe2fd',
        },
      },
    ]
    return {
      isSavingPossible: false,
      isSavingDraftPossible: true,
      whatPreventsSaving,
    }
  }

  // Enabled drafts, if scheduled, should be scheduled for a time in the future
  if (isScheduledInThePast()) {
    const whatPreventsSaving: ValidationFeedback[] = [
      {
        code: COMPOSER_ERROR_CODE.SCHEDULE_IN_THE_PAST,
        message: preventSavingMessages.SCHEDULE_IN_THE_PAST,
        composerId: '',
      },
    ]
    // An overdue draft (scheduled time in the past) cannot be saved by the
    // backend either, so block the save and tell the user why instead of
    // letting the request silently hang. Saving becomes possible again once
    // the user picks a future time. See CORE-5827.
    const whatPreventsSavingDraft: ValidationFeedback[] = [
      {
        code: COMPOSER_ERROR_CODE.SCHEDULE_IN_THE_PAST,
        message: preventSavingMessages.SCHEDULE_IN_THE_PAST_DRAFT,
        composerId: '',
      },
    ]
    return {
      ...savingNotPossible,
      whatPreventsSaving,
      whatPreventsSavingDraft,
    }
  }

  return { isSavingPossible: true, isSavingDraftPossible: true }
}

export const updateIsSavingPossible = (): void => {
  const {
    isSavingDraftPossible,
    whatPreventsSavingDraft = [],
    isSavingPossible,
    whatPreventsSaving = [],
  } = checkIfSavingPossible()

  state.appState.isSavingPossible = isSavingPossible
  state.appState.isSavingDraftPossible = isSavingDraftPossible
  state.appState.whatPreventsSaving = whatPreventsSaving
  state.appState.whatPreventsSavingDraft = whatPreventsSavingDraft
}

const debounceValidation = debounce(
  () => {
    updateIsSavingPossible()
    AppStore.emitChange()
  },
  80,
  {
    leading: true,
    trailing: true,
  },
)

const getExpandedComposerId = (): string | null =>
  state.appState.expandedComposerId

const setExpandedComposerId = (id: string | null): void => {
  if (id !== null && typeof id !== 'undefined') {
    if (ComposerStore.isDraftLocked(id)) return
  }
  state.appState.expandedComposerId = id
}

const expandProfileSubprofileDropdown = (id: string): void => {
  state.appState.expandedProfileSubprofileDropdownId = id

  const idIndex =
    state.appState.profileSubprofileDropdownsIdsToExpand.indexOf(id)
  const wasDropdownWaitingForExpansion = idIndex !== -1

  if (wasDropdownWaitingForExpansion) {
    state.appState.profileSubprofileDropdownsIdsToExpand.splice(idIndex, 1)
  }
}

const setThumbnailLoading = (isLoading: boolean): void => {
  state.appState.isThumbnailLoading = isLoading
}

const collapseProfileSubprofileDropdown = (id: string): void => {
  const isDropdownCurrentlyExpanded =
    state.appState.expandedProfileSubprofileDropdownId === id
  if (!isDropdownCurrentlyExpanded) return

  const dropdownIdsToExpand =
    state.appState.profileSubprofileDropdownsIdsToExpand
  const shouldExpandOtherDropdowns = dropdownIdsToExpand.length > 0

  if (shouldExpandOtherDropdowns) {
    const nextDropdownId = dropdownIdsToExpand[0]
    expandProfileSubprofileDropdown(nextDropdownId)
  } else {
    state.appState.expandedProfileSubprofileDropdownId = null
  }
}

const hasMultipleFacebookChannelsSelected = (): boolean => {
  const hasFacebookGroupSelected =
    state.profiles.filter(
      (profile) =>
        profile.isSelected &&
        profile.serviceType === 'group' &&
        profile.serviceName === 'facebook',
    ).length > 0
  const hasFacebookPageSelected =
    state.profiles.filter(
      (profile) =>
        profile.isSelected &&
        profile.serviceType === 'page' &&
        profile.serviceName === 'facebook',
    ).length > 0
  return hasFacebookGroupSelected && hasFacebookPageSelected
}

const hasMultipleTwitterChannelsSelected = (): boolean => {
  return (
    state.profiles.filter(
      (profile) => profile.isSelected && profile.serviceName === 'twitter',
    ).length >= 2
  )
}

const hasConflictingInstagramChannelsSelected = (): boolean => {
  const hasInstagramBusiness =
    state.profiles.filter(
      (profile) =>
        profile.isSelected &&
        profile.serviceType === 'business' &&
        profile.serviceName === 'instagram',
    ).length > 0
  const hasInstagramProfile =
    state.profiles.filter(
      (profile) =>
        profile.isSelected &&
        profile.serviceType === 'profile' &&
        profile.serviceName === 'instagram',
    ).length > 0
  return hasInstagramBusiness && hasInstagramProfile
}

const queueProfileSubprofilesDropdownsIdsToExpand = (ids: string[]): void => {
  const unselectedProfileIds = ids.filter((id) => {
    const profile = AppStore.getProfile(id)
    return !profile?.isSelected && !profile?.isDisabled
  })
  if (unselectedProfileIds.length === 0) return

  state.appState.profileSubprofileDropdownsIdsToExpand.push(
    ...unselectedProfileIds,
  )

  // Automatically expand first queued profile's subprofiles dropdown
  const firstUnselectedProfileId = unselectedProfileIds[0]
  expandProfileSubprofileDropdown(firstUnselectedProfileId)
}

const emptyProfileSubprofilesDropdownsIdsToExpand = (): void => {
  state.appState.profileSubprofileDropdownsIdsToExpand = []
}

const createNewSubprofile = (
  profileId: string,
  id: string,
  serviceId: string,
  avatar: string,
  name: string,
  isShared: boolean,
): void => {
  const profile = AppStore.getProfile(profileId)

  if (profile) {
    profile.subprofilesOrignatedFromAPI = false
    const subprofile = getNewSubprofile({
      avatar,
      id,
      serviceId,
      name,
      isShared,
    })
    profile.subprofiles.push(subprofile)
  }
}

const refreshSubprofileData = (
  subprofiles: Subprofile[],
  profileId: string,
): void => {
  const profile = AppStore.getProfile(profileId)
  if (profile && profile.subprofiles.length < subprofiles?.length) {
    profile.subprofilesOrignatedFromAPI = true
  }
  if (profile) {
    profile.subprofiles = subprofiles.map((subprofile) =>
      getNewSubprofile(subprofile),
    )
  }
}

const setProfileSubprofileCreationState = (
  profileId: string,
  creationState: keyof typeof AsyncOperationStates,
): void => {
  const profile = AppStore.getProfile(profileId)
  if (!profile) return

  switch (creationState) {
    case AsyncOperationStates.PENDING:
      profile.appState.isSubprofileCreationPending = true
      break

    case AsyncOperationStates.DONE:
      profile.appState.isSubprofileCreationPending = false
      break

    default:
      break
  }
}

const toggleAllProfiles = (): void => {
  const profiles = AppStore.getProfiles()

  const [selectedProfiles, unselectedProfiles]: [
    ComposerProfile[],
    ComposerProfile[],
  ] = partition(profiles, (profile: ComposerProfile) => profile.isSelected)

  const hasSelectedProfiles = selectedProfiles.length > 0

  if (hasSelectedProfiles) {
    const selectedProfilesIds = selectedProfiles.map((profile) => profile.id)
    AppActionCreators.unselectProfiles(selectedProfilesIds)
    AppActionCreators.emptyProfileSubprofilesDropdownsIdsToExpand()
  } else {
    // Preserve last active composer's contents, if it's not empty, by making its profiles
    // the first profiles to be selected (and thus its composer the first to be enabled)
    const { lastInteractedWithComposerId } = ComposerStore.getMeta()
    const unselectedProfilesIds = unselectedProfiles
      .toSorted((profile) =>
        profile.service.name === lastInteractedWithComposerId ? -1 : 1,
      )
      .map((profile) => profile.id)

    profiles.forEach((profile) => {
      if (profile.subprofiles.length > 0) {
        AppActionCreators.selectSubprofile(
          profile.id,
          profile.subprofiles[0].id,
        )
        AppActionCreators.collapseProfileSubprofileDropdown(profile.id)
      }
    })
    AppActionCreators.selectProfilesOnBehalfOfUser(unselectedProfilesIds)
  }
}

const resetPreviouslyExpandedThreadEditor = (
  nextExpandedComposerId?: string | null,
): void => {
  const { expandedComposerId } = state.appState
  const expandedDraft = ComposerStore.getDraft(expandedComposerId)
  const wasOneComposerPreviouslyExpanded = expandedComposerId !== null
  const isReExpandingSameComposer =
    expandedComposerId === nextExpandedComposerId

  if (
    wasOneComposerPreviouslyExpanded &&
    !isReExpandingSameComposer &&
    !AppStore.getIsOmniboxEnabled()
  ) {
    if (expandedDraft?.hasThread()) {
      ComposerActionCreators.switchActiveThreadEditor(expandedDraft.id, 0)
    }
  }
}

const collapseComposer = (): void => {
  if (AppStore.getServicesWithSelectedProfiles().length === 1) return

  resetPreviouslyExpandedThreadEditor()
  setExpandedComposerId(null)
}

const markAppAsLoaded = (): void => {
  state.appState.isLoaded = true

  // Run this on load, then profile selection / link attachment change events take over
  refreshFacebookDomainOwnershipData()
}

const updatePartiallySavedDraftsProfilesIds = (
  partiallySavedDrafts: Array<{ draftId: string; profilesIds: string[] }>,
): void => {
  const { partiallySavedDraftsProfilesIds } = state.appState

  // Merge each draft's previous profiles ids with new ones
  partiallySavedDrafts.forEach(({ draftId, profilesIds }) => {
    partiallySavedDraftsProfilesIds.set(draftId, [
      ...(partiallySavedDraftsProfilesIds.get(draftId) || []),
      ...profilesIds,
    ])
  })
}

const updateFacebookDomainOwnershipForPage = (
  id: string,
  url: string,
  isOwner: boolean,
): void => {
  let domainData = state.appState.domainsOwnedByFacebookPages.get(url)

  if (domainData === undefined) {
    domainData = new Map()
    state.appState.domainsOwnedByFacebookPages.set(url, domainData)
  }

  domainData.set(id, isOwner)
}

const resetProfilesData = (profilesData: ComposerProfile[]): void => {
  AppActionCreators.updateOmniboxState(null)
  AppActionCreators.unselectProfiles(
    AppStore.getSelectedProfiles().map(({ id }) => id),
  )
  const profilesToSelect = profilesData.filter(
    (profile) => profile.shouldBeAutoSelected,
  )

  if (profilesToSelect) {
    AppActionCreators.selectProfilesOnBehalfOfUser(
      profilesToSelect.map((profile) => profile.id),
      true,
    )
  }
}

const updateSelectedProfiles = (
  idsToSelect: string[],
  idsToUnselect: string[],
): void => {
  if (idsToSelect) {
    AppActionCreators.selectProfilesOnBehalfOfUser(idsToSelect)
  }
  if (idsToUnselect) {
    AppActionCreators.unselectProfiles(idsToUnselect)
  }
}

const onDispatchedPayload = (payload: ComposerDispatchPayload): void => {
  const { action } = payload
  let isPayloadInteresting = true

  switch (action.actionType) {
    case ActionTypes.COMPOSER_CREATE_PROFILES:
      state.profiles = action.profiles
      break

    case ActionTypes.APP_SELECT_SUBPROFILE:
      selectSubprofile(action.profileId, action.subprofileId)
      collapseProfileSubprofileDropdown(action.profileId)
      break

    case ActionTypes.APP_SELECT_SUBPROFILES:
      selectSubprofiles(action.idsMap, action.markAppAsLoadedWhenDone)
      action.idsMap.forEach((_: string, profileId: string) => {
        collapseProfileSubprofileDropdown(profileId)
      })
      break

    case ActionTypes.APP_UNSELECT_SUBPROFILE:
      unselectSubprofile(action.profileId)
      collapseProfileSubprofileDropdown(action.profileId)
      break

    case ActionTypes.APP_UPDATE_OMNIBOX_STATE:
      AppStore.setIsOmniboxEnabled(action.isEnabled)
      break

    case ActionTypes.COMPOSER_APPLY_OMNI_UPDATE: {
      // Set expandedComposerId to the first selected profile
      const selectedProfiles = AppStore.getSelectedProfiles()
      if (selectedProfiles?.[0]) {
        setExpandedComposerId(selectedProfiles[0].serviceName)
      }
      break
    }

    case ActionTypes.APP_UPDATE_PARTIALLY_SAVED_DRAFTS_PROFILES_IDS:
      updatePartiallySavedDraftsProfilesIds(action.partiallySavedDrafts)
      break

    case ActionTypes.COMPOSER_SELECT_PROFILES_ON_BEHALF_OF_USER:
      selectProfilesOnBehalfOfUser(
        action.ids,
        action.markAppAsLoadedWhenDone,
        action.originatedFromGroupSelection,
      )
      break

    case ActionTypes.COMPOSER_SELECT_PROFILE:
      selectProfile(action.id)
      maybeAutoExpandComposer()
      break

    case ActionTypes.COMPOSER_SELECT_PROFILES:
      action.ids.forEach((id: string, i: number) => {
        const markAppAsLoadedWhenDone =
          action.markAppAsLoadedWhenDone && i === 0
        selectProfile(
          id,
          markAppAsLoadedWhenDone,
          action.originatedFromGroupSelection,
        )
      })
      maybeAutoExpandComposer()
      break

    case ActionTypes.UPDATE_SHORTENER_FOR_PROFILE:
      updateShortenerForProfile(action.profileId, action.domain)
      break

    case ActionTypes.COMPOSER_UNSELECT_PROFILE:
      unselectProfile(action.id)
      break

    case ActionTypes.COMPOSER_UNSELECT_PROFILES:
      action.ids.forEach(unselectProfile)
      break

    case ActionTypes.COMPOSER_QUEUE_PROFILES_SUBPROFILES_DROPDOWNS_TO_EXPAND:
      queueProfileSubprofilesDropdownsIdsToExpand(action.ids)
      break

    case ActionTypes.COMPOSER_EMPTY_PROFILES_SUBPROFILES_DROPDOWNS_TO_EXPAND:
      emptyProfileSubprofilesDropdownsIdsToExpand()
      break

    case ActionTypes.COMPOSER_EXPAND:
      resetPreviouslyExpandedThreadEditor(action.id)
      setExpandedComposerId(action.id)
      state.appState.composersHaveBeenExpanded = true
      break

    // We consider a composer's contents saved when it's collapsed
    case ActionTypes.COMPOSER_COLLAPSE:
      collapseComposer()
      break

    // Ensure a disabled composer doesn't remain expanded
    case ActionTypes.COMPOSER_DISABLE:
      if (getExpandedComposerId() === action.id) setExpandedComposerId(null)
      break

    case ActionTypes.APP_RECEIVE_METADATA:
      state.metaData = action.metaData
      break

    case ActionTypes.APP_RECEIVE_OPTIONS:
      state.options = action.options
      break

    case ActionTypes.APP_RECEIVE_CONNECTED_CHANNELS:
      state.connectedChannels = action.connectedChannels
      break

    case ActionTypes.COMPOSER_EXPAND_PROFILE_SUBPROFILE_DROPDOWN:
      expandProfileSubprofileDropdown(action.id)
      break

    case ActionTypes.COMPOSER_COLLAPSE_PROFILE_SUBPROFILE_DROPDOWN:
      collapseProfileSubprofileDropdown(action.id)
      break

    case ActionTypes.COMPOSER_CREATE_NEW_SUBPROFILE:
      createNewSubprofile(
        action.profileId,
        action.subprofileId,
        action.subprofileServiceId,
        action.avatar,
        action.name,
        action.isShared,
      )
      setProfileSubprofileCreationState(
        action.profileId,
        AsyncOperationStates.DONE,
      )
      break

    case ActionTypes.APP_REFRESH_SUBPROFILE_DATA:
      refreshSubprofileData(action.subprofileData, action.profileId)
      break

    case ActionTypes.COMPOSER_CREATE_NEW_SUBPROFILE_FAILED:
      setProfileSubprofileCreationState(
        action.profileId,
        AsyncOperationStates.DONE,
      )
      break

    case ActionTypes.COMPOSER_CREATE_NEW_SUBPROFILE_PENDING:
      setProfileSubprofileCreationState(
        action.profileId,
        AsyncOperationStates.PENDING,
      )
      break

    case ActionTypes.APP_TOGGLE_ALL_PROFILES:
      toggleAllProfiles()
      break

    case ActionTypes.APP_SET_THUMBNAIL_LOADING:
      setThumbnailLoading(action.isLoading)
      break

    case ActionTypes.APP_LOADED:
      markAppAsLoaded()
      break

    case ActionTypes.APP_RESET:
      state = getInitialState()
      break

    case ActionTypes.APP_REFRESH_FACEBOOK_DOMAIN_OWNERSHIP_DATA:
      refreshFacebookDomainOwnershipData()
      break

    case ActionTypes.APP_RECEIVE_FACEBOOK_DOMAIN_OWNERSHIP_DATA:
      updateFacebookDomainOwnershipForPage(
        action.profileId,
        action.url,
        action.isOwner,
      )
      break

    case ActionTypes.COMPOSER_ENABLE:
      if (action.id === 'facebook') refreshFacebookDomainOwnershipData()
      break

    case ActionTypes.RESET_PROFILES_DATA:
      resetProfilesData(action.profilesData)
      break

    case ActionTypes.UPDATE_SELECTED_PROFILES:
      updateSelectedProfiles(action.idsToSelect, action.idsToUnselect)
      break

    /**
     * Changes that have an impact on state.appState.isSavingPossible and happening
     * outside of a composer's expanded state need to dispatch actions that we'll
     * explicitly listen to here in order to react to those changes. Same applies
     * to changes that'd require App to re-render, we need to listen to those too.
     */
    case ActionTypes.COMPOSER_DRAFT_ATTACHMENT_TOGGLED:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_COMMENT_CHARACTER_COUNT:
    case ActionTypes.COMPOSER_DRAFT_IMAGE_ADDED:
    case ActionTypes.COMPOSER_APPLY_IMAGE_WORKSPACE_CHANGES:
    case ActionTypes.COMPOSER_REPLACE_DRAFT_GIF:
    case ActionTypes.COMPOSER_DRAFT_VIDEO_ADDED:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_VIDEO_TITLE:
    case ActionTypes.COMPOSER_DRAFT_GIF_ADDED:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_GIF_FRAME_COUNT:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_UPDATE_TYPE:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_SELECTED_STICKERS:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_GOOGLE_BUSINESS_DATA:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_MASTODON_DATA:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_START_PAGE_DATA:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_INSTAGRAM_DATA:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_LINKEDIN_DATA:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_FACEBOOK_DATA:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_YOUTUBE_DATA:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_TIKTOK_DATA:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_THREADS_DATA:
    case ActionTypes.COMPOSER_SET_LINKEDIN_CAROUSEL_TITLE:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_TITLE_CHARACTER_COUNT:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_SOURCE_LINK:
    case ActionTypes.COMPOSER_UPDATE_DRAFTS_SOURCE_LINK:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_SOURCE_LINK_DATA:
    case ActionTypes.COMPOSER_DELETE_THREADED_DRAFT:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_MEDIA_ORDER:
    case ActionTypes.COMPOSER_SWITCH_ACTIVE_THREAD_EDITOR:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_ADD_POST_TO_THREAD:
    case ActionTypes.COMPOSER_UPDATE_POST_TEXT:
    case ActionTypes.COMPOSER_DRAFT_UPDATED:
    case ActionTypes.PROFILE_DROPDOWN_HIDDEN:
    case ActionTypes.UPDATE_DRAFT_HAS_SAVING_ERROR:
    case ActionTypes.COMPOSER_UPDATE_DRAFT_IS_REMINDER:
    case ActionTypes.COMPOSER_REMOVE_DRAFT_DOCUMENT:
    case ActionTypes.COMPOSER_UPDATE_DRAFTS_SCHEDULED_AT:
    // Load-time attach paths for a reopened draft's existing media (incl.
    // async dimension arrival); without these a draft loaded with an
    // already-invalid attachment keeps a stale isSavingPossible until some
    // unrelated action triggers a recompute.
    case ActionTypes.COMPOSER_ADD_DRAFTS_AUTO_UPLOADED_IMAGE:
    case ActionTypes.COMPOSER_ADD_DRAFTS_AUTO_UPLOADED_GIF:
    case ActionTypes.COMPOSER_ADD_DRAFTS_AUTO_UPLOADED_VIDEO:
    case ActionTypes.COMPOSER_ADD_DRAFTS_AUTO_UPLOADED_VIDEOS:
    case ActionTypes.COMPOSER_ADD_DRAFT_UPLOADED_DOCUMENT:
    case ActionTypes.COMPOSER_UPDATE_UPLOADED_IMAGE_DIMENSIONS:
      isPayloadInteresting = true
      break

    case ActionTypes.COMPOSER_UPDATE_SIDEPANEL_ONTOP:
      state.appState.rightSidePanelVisibleOnTop = action.sidebarOnTop
      isPayloadInteresting = true
      break

    default:
      isPayloadInteresting = false
      break
  }

  if (isPayloadInteresting) {
    // todo: consider using rAF at the component listening to this store
    setTimeout(() => {
      AppStore.emitChange()
      debounceValidation()
    }, 0)
  }

  // (uncomment to enable Redux DevTools)
  // sendToMonitor('app', action, state);
}

AppDispatcher.register(onDispatchedPayload)

export default AppStore
